/**
 * Utilidades puras para editar los vértices de una geocerca ([lat, lng]).
 * Sin dependencia de Leaflet para poder probarlas directamente.
 */
import type { GeoJSONPolygon } from '../types/api.types';

export type LatLng = [number, number];

/** Mínimo de vértices de un polígono válido. */
export const MIN_VERTICES = 3;

/** Convierte el anillo exterior GeoJSON ([lng,lat], cerrado) a vértices [lat,lng] sin el punto de cierre repetido. */
export function boundaryToVertices(boundary?: GeoJSONPolygon | null): LatLng[] {
  const ring = boundary?.coordinates?.[0];
  if (!ring || ring.length === 0) return [];
  const pts = ring.map(([lng, lat]) => [lat, lng] as LatLng);
  const first = pts[0];
  const last = pts[pts.length - 1];
  if (pts.length > 1 && first[0] === last[0] && first[1] === last[1]) pts.pop();
  return pts;
}

/** Punto medio de la arista `i` (del vértice i al i+1, cerrando con el primero). */
export function edgeMidpoint(points: LatLng[], edgeIndex: number): LatLng {
  const a = points[edgeIndex];
  const b = points[(edgeIndex + 1) % points.length];
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
}

/** Puntos medios de todas las aristas (requiere >= 3 vértices; si no, vacío). */
export function edgeMidpoints(points: LatLng[]): LatLng[] {
  if (points.length < MIN_VERTICES) return [];
  return points.map((_, i) => edgeMidpoint(points, i));
}

/** Inserta un vértice en el punto medio de la arista `edgeIndex`; el nuevo vértice queda en edgeIndex+1. */
export function insertMidpoint(points: LatLng[], edgeIndex: number): LatLng[] {
  if (edgeIndex < 0 || edgeIndex >= points.length || points.length < 2) return points;
  const next = [...points];
  next.splice(edgeIndex + 1, 0, edgeMidpoint(points, edgeIndex));
  return next;
}

/** Borra el vértice `index`; si quedarían menos de MIN_VERTICES devuelve el arreglo sin cambios. */
export function removeVertex(points: LatLng[], index: number): LatLng[] {
  if (index < 0 || index >= points.length || points.length <= MIN_VERTICES) return points;
  return points.filter((_, i) => i !== index);
}

/**
 * Traslada todos los vértices (arrastrar la geocerca completa). El
 * desplazamiento se recorta para que ningún vértice salga de [-90, 90] de
 * latitud; la longitud se normaliza a [-180, 180]. La forma y el área (a
 * esta escala) no cambian, así que los topes de superficie siguen valiendo.
 */
export function translatePoints(points: LatLng[], dLat: number, dLng: number): LatLng[] {
  if (points.length === 0) return points;
  const lats = points.map(p => p[0]);
  const safeDLat = Math.min(Math.max(dLat, -90 - Math.min(...lats)), 90 - Math.max(...lats));
  const wrap = (lng: number) => ((((lng + 180) % 360) + 360) % 360) - 180;
  return points.map(([lat, lng]) => [lat + safeDLat, wrap(lng + dLng)] as LatLng);
}
